import { honoursInjuredToggle } from "../../../Processing/countSpec.mjs";
import { Checkbox, Switch, Typography } from "antd";
import { usePlotContext } from "../PlotContext";
import type { GraphFormState } from "../plotState";
import { PlayersForm } from "../../shared/PlayersForm";
import { isSectionBindable } from "../../../Processing/controllerBindings";

const { Text } = Typography;

interface PlayersStepProps {
  graph: GraphFormState;
  graphIndex: number;
  onChange: (patch: Partial<GraphFormState>) => void;
}

export function PlayersStep({ graph, graphIndex, onChange }: PlayersStepProps) {
  const { getSubjectEntities } = usePlotContext();
  const showInherit = graphIndex >= 1;

  // The backend ignores the toggle for these two: an injury row's date sits inside its
  // own spell, so applying it would report zero in every bucket, and a treatment count
  // would drop the treatments an injured athlete is the most likely to have. Flags and
  // statuses honour it as usual.
  const ignoresInjuredToggle =
    graph.countSpec != null && !honoursInjuredToggle(graph.countSpec.source);

  return (
    <div className="awm-step-body">
      {showInherit && (
        <Checkbox
          checked={graph.inheritPlayers}
          onChange={(e) => onChange({ inheritPlayers: e.target.checked })}
          style={{
            padding: "8px 10px",
            border: `1px solid ${graph.inheritPlayers ? "#b0c9f0" : "#e0e5eb"}`,
            borderRadius: 8,
            background: graph.inheritPlayers ? "#f0f5ff" : "#f7f9fb",
            width: "100%",
          }}
        >
          <div>
            <Text strong style={{ fontSize: 12 }}>Same as Plot 1</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Uses the same athlete selection. Uncheck to pick different players.
            </Text>
          </div>
        </Checkbox>
      )}

      {(!showInherit || !graph.inheritPlayers) && (
        <>
          <PlayersForm
            hideDrills={!!graph.countSpec}
            value={graph}
            onChange={onChange}
            getSubjectEntities={getSubjectEntities}
            bindable={isSectionBindable("chart", "subject")}
          />
          <div
            style={{
              display: "flex", justifyContent: "space-between", alignItems: "flex-start",
              padding: "8px 10px", border: "1px solid #e0e5eb", borderRadius: 8, background: "#fafbfc",
            }}
          >
            <div>
              <Text strong style={{ fontSize: 12 }}>Exclude Injured Players</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {ignoresInjuredToggle
                  ? "Not available for this metric: it counts the very records the toggle would remove."
                  : graph.countSpec != null
                    ? "Hide athletes with an active injury from this chart."
                    : "Hide injured athletes' rows."}
              </Text>
            </div>
            <Switch
              size="small"
              disabled={ignoresInjuredToggle}
              checked={ignoresInjuredToggle ? false : graph.excludeInjuredPlayers}
              onChange={(v) => onChange({ excludeInjuredPlayers: v })}
            />
          </div>
        </>
      )}
    </div>
  );
}
